import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw, Mail, Phone, MapPin, Calendar as CalIcon } from "lucide-react";
import api from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUSES = ["new", "contacted", "confirmed", "completed", "cancelled"];

const STATUS_STYLES = {
  new: "bg-melissa-rose/10 text-melissa-rose border-melissa-rose/30",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-melissa-ink text-melissa-bg border-melissa-ink",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, confirmed: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const adminEmail = typeof window !== "undefined" ? localStorage.getItem("melissa_admin_email") : "";

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/bookings"), api.get("/bookings/stats")]);
      setBookings(b.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("melissa_admin_token");
        nav("/admin/login");
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    const token = localStorage.getItem("melissa_admin_token");
    if (!token) {
      nav("/admin/login");
      return;
    }
    loadAll();
  }, [loadAll, nav]);

  const logout = () => {
    localStorage.removeItem("melissa_admin_token");
    localStorage.removeItem("melissa_admin_email");
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success(`Status updated to ${status}`);
      loadAll();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking removed");
      loadAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const visible = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-melissa-bg2">
      {/* Top bar */}
      <header className="bg-melissa-bg border-b border-melissa-border sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="admin-back-to-site" className="flex flex-col leading-none">
            <span className="font-serif text-xl tracking-tight text-melissa-ink">Melissa O'Donnell</span>
            <span className="text-[10px] tracking-overline uppercase text-melissa-inkSoft mt-1">Admin Console</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-melissa-inkSoft">{adminEmail}</span>
            <button
              onClick={loadAll}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-overline border border-melissa-border hover:border-melissa-ink"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-overline bg-melissa-ink text-melissa-bg hover:bg-melissa-rose transition-colors"
            >
              <LogOut size={12} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <p className="text-xs uppercase tracking-overline text-melissa-rose mb-4">Inquiries & Bookings</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl text-melissa-ink leading-tight tracking-tight mb-10">
          The Studio Journal
        </h1>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {[
            ["Total", stats.total || 0, "total"],
            ["New", stats.new || 0, "new"],
            ["Contacted", stats.contacted || 0, "contacted"],
            ["Confirmed", stats.confirmed || 0, "confirmed"],
            ["Completed", stats.completed || 0, "completed"],
          ].map(([label, val, key]) => (
            <div
              key={key}
              data-testid={`stat-card-${key}`}
              className="bg-melissa-bg border border-melissa-border p-6"
            >
              <div className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">{label}</div>
              <div className="font-serif text-4xl text-melissa-ink mt-2">{val}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 text-[10px] uppercase tracking-overline border transition-colors ${
                filter === s
                  ? "bg-melissa-ink text-melissa-bg border-melissa-ink"
                  : "bg-transparent text-melissa-ink border-melissa-border hover:border-melissa-ink"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Bookings list */}
        {loading ? (
          <div data-testid="admin-loading" className="text-melissa-inkSoft text-sm italic">Loading…</div>
        ) : visible.length === 0 ? (
          <div data-testid="admin-empty" className="bg-melissa-bg border border-melissa-border p-12 text-center">
            <p className="font-serif text-2xl text-melissa-ink">No inquiries yet</p>
            <p className="text-sm text-melissa-inkSoft mt-2">When clients submit the contact form, they'll appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="bg-melissa-bg border border-melissa-border p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6"
              >
                <div className="md:col-span-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      data-testid={`booking-status-${b.id}`}
                      className={`text-[10px] uppercase tracking-overline px-2 py-1 border ${STATUS_STYLES[b.status] || ""}`}
                    >
                      {b.status}
                    </span>
                    <span className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">
                      {new Date(b.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="font-serif text-2xl text-melissa-ink">{b.name}</div>
                  <div className="text-sm text-melissa-rose mt-1">{b.service}</div>
                  <div className="mt-3 inline-flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Deposit</span>
                    <span
                      data-testid={`booking-deposit-${b.id}`}
                      className={`text-[10px] uppercase tracking-overline px-2 py-1 border ${
                        b.deposit_status === "paid"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-zinc-100 text-zinc-500 border-zinc-200"
                      }`}
                    >
                      {b.deposit_status || "pending"}
                    </span>
                    {b.upsell_status && b.upsell_status !== "none" && (
                      <span
                        data-testid={`booking-upsell-${b.id}`}
                        className={`text-[10px] uppercase tracking-overline px-2 py-1 border ${
                          b.upsell_status === "paid"
                            ? "bg-melissa-rose/10 text-melissa-rose border-melissa-rose/30"
                            : "bg-zinc-100 text-zinc-500 border-zinc-200"
                        }`}
                      >
                        Bundle · {b.upsell_status}
                      </span>
                    )}
                    {(b.pending_credits || 0) > 0 && (
                      <span
                        data-testid={`booking-credits-${b.id}`}
                        className="text-[10px] uppercase tracking-overline px-2 py-1 border bg-amber-50 text-amber-800 border-amber-200"
                      >
                        ${(b.pending_credits || 0).toFixed(0)} credit
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-overline text-melissa-inkSoft">
                    <span data-testid={`booking-refcode-${b.id}`}>
                      Code: <span className="font-mono text-melissa-ink">{b.referral_code || "—"}</span>
                    </span>
                    {b.referred_by && (
                      <span data-testid={`booking-refby-${b.id}`}>
                        Referred by: <span className="font-mono text-melissa-rose">{b.referred_by}</span>
                      </span>
                    )}
                  </div>
                </div>

                <div className="md:col-span-5 space-y-2 text-sm text-melissa-inkSoft">
                  <div className="flex items-center gap-2">
                    <Mail size={14} />
                    <a href={`mailto:${b.email}`} className="hover:text-melissa-ink">{b.email}</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={14} />
                    <a href={`tel:${b.phone}`} className="hover:text-melissa-ink">{b.phone}</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalIcon size={14} />
                    <span>{b.appointment_date} · {b.appointment_time}</span>
                  </div>
                  {b.location && (
                    <div className="flex items-center gap-2">
                      <MapPin size={14} />
                      <span>{b.location}</span>
                    </div>
                  )}
                  {b.message && (
                    <p className="pt-3 italic font-serif text-melissa-ink/80 text-base leading-snug border-t border-melissa-border mt-3">
                      "{b.message}"
                    </p>
                  )}
                </div>

                <div className="md:col-span-3 flex md:flex-col items-start md:items-end gap-3 justify-between md:justify-start">
                  <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                    <SelectTrigger
                      data-testid={`booking-status-select-${b.id}`}
                      className="w-full md:w-44 bg-melissa-bg border-melissa-border rounded-none h-10 text-xs uppercase tracking-overline"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`booking-delete-${b.id}`}
                        className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-overline border border-melissa-border text-melissa-inkSoft hover:text-red-700 hover:border-red-300"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently removes {b.name}'s inquiry. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`booking-delete-cancel-${b.id}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid={`booking-delete-confirm-${b.id}`}
                          onClick={() => remove(b.id)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
